import { Location } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Interviewee, RxjsComponent } from '@smooved/core';
import { UiSandbox, WizardService } from '@smooved/ui';
import { Observable } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { SURVEY_CONFIG } from '../../constants/surveys.constants';
import { SurveyConfig } from '../../interfaces/survey-config.interface';
import { SurveysSandbox } from '../../sandboxes/surveys.sandbox';
import { SurveyService } from '../../services/survey.service';

@Component({
    selector: 'app-survey-interview-container',
    templateUrl: './survey-interview.container.html',
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class SurveyInterviewContainer extends RxjsComponent implements OnInit {
    public page$ = this.route.params.pipe(
        switchMap(() => this.surveySandbox.interviewOnce$),
        map((interview) => {
            if (this.uiSandbox.isBrowser()) document.body.scrollIntoView({ block: 'start' });
            const pageId = this.route.snapshot.paramMap.get('page');
            return pageId ? interview?.pages.find((page): boolean => page.id === pageId) : interview?.pages[0];
        }, takeUntil(this.destroy$))
    );

    public stepStart$ = this.route.params.pipe(
        switchMap(() => this.surveySandbox.interviewOnce$),
        map((interview) => {
            const pageId = this.route.snapshot.paramMap.get('page');
            return (pageId ? interview?.pages.findIndex((page): boolean => page.id === pageId) : 0) + 1;
        }, takeUntil(this.destroy$))
    );

    public stepEnd$ = this.surveySandbox.interviewOnce$.pipe(
        map((interview) => {
            if (interview) {
                this.wizardService.setSteps(interview.pages?.map((page): string => page.id));
            }
            return interview?.pages.length;
        })
    );

    public interviewee: string;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly location: Location,
        private readonly surveySandbox: SurveysSandbox,
        private readonly surveyService: SurveyService,
        private readonly wizardService: WizardService,
        private readonly uiSandbox: UiSandbox,
        @Optional() @Inject(SURVEY_CONFIG) public readonly config: SurveyConfig
    ) {
        super();
    }

    public ngOnInit(): void {
        this.interviewee = this.route.snapshot.paramMap.get('interviewee') as Interviewee;
        this.surveySandbox.interviewOnce$.subscribe((interview) => {
            if (!interview) this.surveyService.onSurveyNotFound();
        });
    }

    public onPrevious(): void {
        this.location.back();
    }

    public onNext(): void {
        const pageId = this.route.snapshot.paramMap.get('page');
        this.surveySandbox.interviewOnce$.subscribe((interview) => {
            if (interview?.pages?.findIndex((page): boolean => page.id === pageId) >= interview?.pages.length - 1) {
                this.surveySandbox
                    .getAnswers()
                    .pipe(
                        take(1),
                        switchMap((answers): Observable<boolean> => {
                            return this.surveyService.onSurveyComplete(answers);
                        })
                    )
                    .subscribe((success) => {
                        if (!success) return;
                        this.surveyService.afterSurveyComplete();
                    });
                return;
            }
            void this.router.navigate(
                [pageId ? '../' : './', interview.pages[interview?.pages?.findIndex((page): boolean => page.id === pageId) + 1 || 1]?.id],
                { relativeTo: this.route }
            );
        });
    }
}
